import React from 'react';
import { Card, Table, Typography, Empty, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { rejectionService, type RejectionLogDTO } from '@/services/rejection.service';

const { Text } = Typography;

export interface RejectionHistoryPanelProps {
  documentType: string;
  documentId: string;
  title?: string;
}

export const RejectionHistoryPanel: React.FC<RejectionHistoryPanelProps> = ({
  documentType,
  documentId,
  title = 'Rejection history',
}) => {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['rejection-logs', documentType, documentId],
    queryFn: () => rejectionService.listLogsForDocument(documentType, documentId),
    enabled: !!documentType && !!documentId,
  });

  const columns: ColumnsType<RejectionLogDTO> = [
    {
      title: 'When',
      dataIndex: 'rejected_at',
      width: 180,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Reason',
      key: 'reason',
      render: (_, r) => (
        <span>
          <Text strong>{r.rejection_reason_text || r.rejection_reason_code}</Text>
          {r.rejection_reason_code ? <Text type="secondary"> ({r.rejection_reason_code})</Text> : null}
        </span>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Document #',
      dataIndex: 'document_number',
      width: 140,
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
  ];

  return (
    <Card size="small" title={title}>
      <Spin spinning={isLoading}>
        {!isLoading && rows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No rejection records for this document" />
        ) : (
          <Table<RejectionLogDTO>
            rowKey="id"
            size="small"
            pagination={false}
            columns={columns}
            dataSource={rows}
          />
        )}
      </Spin>
    </Card>
  );
};
